/**
 * The filled media slots (#1719 — fill brief
 * `docs/agent-prompts/handover-feature-pages-media-2026-08-01.md`).
 *
 * One entry per slot id that has a real asset in `web/public/features/`
 * (served at `/features/...`). `MediaSlot` consults this map: an id
 * with an entry renders the asset INSIDE the same hairline frame; an
 * id without one keeps the deliberate placeholder. Filling a slot is
 * therefore: drop the file(s), add the entry — the component and the
 * pages never change.
 *
 * Naming (per the brief): `<slot-id>.<ext>`. The `composite` shape
 * takes TWO captures — `<slot-id>-browser.<ext>` + `<slot-id>-phone.<ext>`
 * — because it renders a browser frame with an overlapping phone frame;
 * the slot id stays the single key.
 *
 * Every asset must satisfy the brief's constraints: sage theme, a
 * made-up demo team (Meridian General or Harbourline Security), never a real customer's names, banlist-clean
 * pixels, legible at rendered size (crop to the surface), ≲2 MB,
 * videos muted/loop/autoplay/playsinline with a poster.
 */

export type MediaAsset =
  | { kind: "image"; src: string }
  | { kind: "video"; src: string; poster: string }
  | { kind: "composite"; browser: string; phone: string };

export const MEDIA_ASSETS: Readonly<Record<string, MediaAsset>> = {
  // Filled by the capture passes — keep alphabetised by slot id.
  "member-calendar": { kind: "image", src: "/features/member-calendar.png" },
  "member-inbox": { kind: "image", src: "/features/member-inbox.png" },
  "member-showcase": {
    kind: "composite",
    browser: "/features/member-showcase-browser.png",
    phone: "/features/member-showcase-phone.png",
  },
  "member-task-chip": { kind: "image", src: "/features/member-task-chip.png" },
  "member-tasks": { kind: "image", src: "/features/member-tasks.png" },
  "member-timeblocks": { kind: "image", src: "/features/member-timeblocks.png" },
  "member-walkthrough": {
    kind: "video",
    src: "/features/member-walkthrough.mp4",
    poster: "/features/member-walkthrough-poster.jpg",
  },
  "publishing-history": { kind: "image", src: "/features/publishing-history.png" },
  "publishing-notice": { kind: "image", src: "/features/publishing-notice.png" },
  "publishing-release": { kind: "image", src: "/features/publishing-release.png" },
  "publishing-showcase": {
    kind: "composite",
    browser: "/features/publishing-showcase-browser.png",
    phone: "/features/publishing-showcase-phone.png",
  },
  "publishing-workload": { kind: "image", src: "/features/publishing-workload.png" },
  "requests-categories": { kind: "image", src: "/features/requests-categories.png" },
  "requests-flow": { kind: "image", src: "/features/requests-flow.png" },
  "requests-flow-steps": { kind: "image", src: "/features/requests-flow-steps.png" },
  "requests-queue": { kind: "image", src: "/features/requests-queue.png" },
  "requests-showcase": {
    kind: "composite",
    browser: "/features/requests-showcase-browser.png",
    phone: "/features/requests-showcase-phone.png",
  },
  "requests-submissions": { kind: "image", src: "/features/requests-submissions.png" },
  "setup-guided": { kind: "image", src: "/features/setup-guided.png" },
  "setup-stations": { kind: "image", src: "/features/setup-stations.png" },
  "solver-console": {
    kind: "video",
    src: "/features/solver-console.mp4",
    poster: "/features/solver-console-poster.jpg",
  },
  "solver-corrections": { kind: "image", src: "/features/solver-corrections.png" },
  "solver-gap": { kind: "image", src: "/features/solver-gap.png" },
  "solver-grid": { kind: "image", src: "/features/solver-grid.png" },
  "solver-groups": { kind: "image", src: "/features/solver-groups.png" },
  "solver-progress": { kind: "image", src: "/features/solver-progress.png" },
  "solver-showcase": {
    kind: "composite",
    browser: "/features/solver-showcase-browser.png",
    phone: "/features/solver-showcase-phone.png",
  },
  "solver-versions": { kind: "image", src: "/features/solver-versions.png" },
  "solver-views": { kind: "image", src: "/features/solver-views.png" },
  "swaps-board": { kind: "image", src: "/features/swaps-board.png" },
  "swaps-drag": {
    kind: "video",
    src: "/features/swaps-drag.mp4",
    poster: "/features/swaps-drag-poster.jpg",
  },
  "swaps-flow": {
    kind: "video",
    src: "/features/swaps-flow.mp4",
    poster: "/features/swaps-flow-poster.jpg",
  },
  "swaps-raise": { kind: "image", src: "/features/swaps-raise.png" },
  "swaps-showcase": {
    kind: "composite",
    browser: "/features/swaps-showcase-browser.png",
    phone: "/features/swaps-showcase-phone.png",
  },
};
